/**
 * Typed client for the floor-plan-editor API.
 *
 * Mirrors openapi/openapi.yaml. The one piece of real behaviour here is
 * RevisionConflictError: a save rejected because someone else committed first
 * is a normal, recoverable event the editor has to handle, not a failure to
 * swallow.
 */

import { config } from '../config';
import { freshToken } from '../keycloak';
import type { PlanDocument } from '../model/types';
import type { PlanMetrics } from '../model/rooms';

export interface Project {
  id: string;
  name: string;
  address?: string;
  ownerSub: string;
  createdAt: string;
  updatedAt: string;
}

export interface Scenario {
  id: string;
  projectId: string;
  name: string;
  forkedFrom?: string;
  forkedAtRevision?: number;
  headRevision: number;
  createdAt: string;
  updatedAt: string;
}

export interface Revision {
  scenarioId: string;
  seq: number;
  parentSeq?: number;
  authorSub?: string;
  message?: string;
  metrics?: PlanMetrics;
  createdAt: string;
}

export interface DocumentEnvelope {
  scenarioId: string;
  revision: number;
  document: PlanDocument;
  metrics?: PlanMetrics;
}

export interface Asset {
  id: string;
  projectId: string;
  filename: string;
  contentType: string;
  size: number;
  createdAt: string;
}

/** What an extraction found, so the result can be judged rather than trusted. */
export interface ExtractionStats {
  walls: number;
  junctions: number;
  doors: number;
  windows: number;
  openings: number;
  mmPerPixel: number;
  scaleBasis: string;
  orthogonalPercent?: number;
}

/** Thrown when a commit's base revision is no longer the head. */
export class RevisionConflictError extends Error {
  constructor(readonly headRevision: number) {
    super(`The plan moved on to revision ${headRevision} while you were editing.`);
    this.name = 'RevisionConflictError';
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await freshToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });

  if (response.status === 204) return undefined as T;
  if (response.ok) return (await response.json()) as T;

  const body = await response.json().catch(() => ({ error: response.statusText }));
  if (response.status === 409 && typeof body.headRevision === 'number') {
    throw new RevisionConflictError(body.headRevision);
  }
  throw new ApiError(response.status, body.error ?? response.statusText);
}

export const api = {
  me: () => request<{ sub: string; username?: string; name?: string }>('/api/v1/me'),

  listProjects: () =>
    request<{ projects: Project[] }>('/api/v1/projects').then((r) => r.projects),

  createProject: (name: string, address?: string) =>
    request<Project>('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify({ name, address }),
    }),

  updateProject: (projectId: string, patch: { name?: string; address?: string }) =>
    request<Project>(`/api/v1/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteProject: (projectId: string) =>
    request<void>(`/api/v1/projects/${projectId}`, { method: 'DELETE' }),

  listScenarios: (projectId: string) =>
    request<{ scenarios: Scenario[] }>(`/api/v1/projects/${projectId}/scenarios`).then(
      (r) => r.scenarios,
    ),

  createScenario: (projectId: string, name: string, forkedFrom?: string) =>
    request<Scenario>(`/api/v1/projects/${projectId}/scenarios`, {
      method: 'POST',
      body: JSON.stringify({ name, forkedFrom }),
    }),

  updateScenario: (scenarioId: string, patch: { name?: string }) =>
    request<Scenario>(`/api/v1/scenarios/${scenarioId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteScenario: (scenarioId: string) =>
    request<void>(`/api/v1/scenarios/${scenarioId}`, { method: 'DELETE' }),

  getDocument: (scenarioId: string, revision: number | 'latest' = 'latest') =>
    request<DocumentEnvelope>(
      `/api/v1/scenarios/${scenarioId}/document?revision=${revision}`,
    ),

  commit: (
    scenarioId: string,
    baseRevision: number,
    document: PlanDocument,
    metrics: PlanMetrics,
    message?: string,
  ) =>
    request<Revision>(`/api/v1/scenarios/${scenarioId}/revisions`, {
      method: 'POST',
      body: JSON.stringify({ baseRevision, document, metrics, message }),
    }),

  listRevisions: (scenarioId: string) =>
    request<{ revisions: Revision[] }>(`/api/v1/scenarios/${scenarioId}/revisions`).then(
      (r) => r.revisions,
    ),

  getRevision: (scenarioId: string, seq: number) =>
    request<DocumentEnvelope>(`/api/v1/scenarios/${scenarioId}/revisions/${seq}`),

  restoreRevision: (scenarioId: string, seq: number) =>
    request<Revision>(`/api/v1/scenarios/${scenarioId}/revisions/${seq}/restore`, {
      method: 'POST',
    }),

  getMetrics: (scenarioId: string, revision: number | 'latest' = 'latest') =>
    request<PlanMetrics>(`/api/v1/scenarios/${scenarioId}/metrics?revision=${revision}`),

  listAssets: (projectId: string) =>
    request<{ assets: Asset[] }>(`/api/v1/projects/${projectId}/assets`).then((r) => r.assets),

  uploadAsset: (projectId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Asset>(`/api/v1/projects/${projectId}/assets`, {
      method: 'POST',
      body: form,
    });
  },

  /**
   * Reads walls, doors and windows out of a plan image and stores the result
   * as a new scenario. Synchronous: it takes about a second.
   */
  extractPlan: (projectId: string, assetId: string, name?: string) =>
    request<{ scenario: Scenario; stats: ExtractionStats }>(
      `/api/v1/projects/${projectId}/assets/${assetId}/extract`,
      { method: 'POST', body: JSON.stringify({ name }) },
    ),

  deleteAsset: (assetId: string) =>
    request<void>(`/api/v1/assets/${assetId}`, { method: 'DELETE' }),

  /**
   * Assets need the bearer token, so they cannot simply be an <img src>. The
   * caller owns the returned object URL and must revoke it.
   */
  assetObjectUrl: async (assetId: string): Promise<string> => {
    const token = await freshToken();
    const response = await fetch(`${config.apiBaseUrl}/api/v1/assets/${assetId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new ApiError(response.status, 'Could not load the plan image');
    return URL.createObjectURL(await response.blob());
  },
};
